import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthBoot } from '@shared/hooks/use-me';
import { RootLayout } from './app/layouts/root-layout';
import { LandingPage } from './app/routes/landing';
import { NotFoundPage } from './app/routes/not-found';
import { LoginPage } from './features/auth/web/login-page';
import { SignupPage } from './features/auth/web/signup-page';
import { OtpPage } from './features/auth/web/otp-page';
import { ChooseAccountPage } from './features/auth/web/choose-account-page';
import { ClientLayout } from './features/client/web/client-layout';
import { HomePage } from './features/client/web/home-page';
import { ServicesPage } from './features/client/web/services-page';
import { ServiceDetailPage } from './features/client/web/service-detail-page';
import { OnboardingPage } from './features/client/web/onboarding-page';
import { ProfilePage } from './features/client/web/profile-page';
import { ChatPage } from './features/client/web/chat/chat-page';
import { ShareTargetPage } from './features/client/web/share-target-page';
import { MenuPage } from './features/client/web/menu-page';
import { DocumentWalletPage } from './features/client/web/document-wallet-page';
import { FontPreferencePage } from './features/client/web/font-preference-page';
import { EmiCalculatorPage } from './features/client/web/emi-calculator-page';
import { AssociateLayout } from './features/associate/web/associate-layout';
import { AssociateJoinPage } from './features/associate/web/associate-join-page';
import { AssociateLoginPage } from './features/associate/web/associate-login-page';
import { AssociateOnboardingPage } from './features/associate/web/associate-onboarding-page';
import { TransactionsPage as AssociateTransactionsPage } from './features/associate/web/transactions-page';
import { AnalyticsPage as AssociateAnalyticsPage } from './features/associate/web/analytics-page';
import { KycPage as AssociateKycPage } from './features/associate/web/kyc-page';
import { AssociateChatPage } from './features/associate/web/chat-page';
import { AssociateProfilePage } from './features/associate/web/associate-profile-page';
import { AssociateReviewsPage } from './features/associate/web/associate-reviews-page';
import { ServicesOfferedPage } from './features/associate/web/services-offered-page';

// Admin UI lives in the sibling `ecregi-admin` Vite app at admin.ecregi.com —
// intentionally isolated from this public bundle for security (IP allowlist,
// MFA, separate TLS / cookies). See docs/admin-app-architecture.md.
export function App() {
  return (
    <>
      <AuthBoot />
      <Routes>
        {/* Full-viewport auth flows */}
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignupPage />} />
        <Route path="otp" element={<OtpPage />} />
        <Route path="choose-account" element={<ChooseAccountPage />} />
        <Route path="associate/join" element={<AssociateJoinPage />} />
        <Route path="associate/login" element={<AssociateLoginPage />} />

        {/* Public landing */}
        <Route element={<RootLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="404" element={<NotFoundPage />} />
        </Route>

        {/* Onboarding (post-SIGNUP_REQUIRED) runs outside role layouts — user
          is not yet authenticated with a role when these pages are reached. */}
        <Route path="client/onboarding" element={<OnboardingPage />} />
        <Route path="associate/onboarding" element={<AssociateOnboardingPage />} />

        {/* Client app (Phase 1) — auth-guarded */}
        <Route path="client" element={<ClientLayout />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<HomePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/:id" element={<ServiceDetailPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="chat/:threadId" element={<ChatPage />} />
          <Route path="share" element={<ShareTargetPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="wallet" element={<DocumentWalletPage />} />
          <Route path="emi-calculator" element={<EmiCalculatorPage />} />
          <Route path="menu" element={<MenuPage />} />
          <Route path="font-preference" element={<FontPreferencePage />} />
        </Route>

        {/* Associate app (Phase 2) — auth + role-guarded */}
        <Route path="associate" element={<AssociateLayout />}>
          <Route index element={<Navigate to="transactions" replace />} />
          <Route path="transactions" element={<AssociateTransactionsPage />} />
          <Route path="analytics" element={<AssociateAnalyticsPage />} />
          <Route path="services" element={<ServicesOfferedPage />} />
          <Route path="wallet" element={<DocumentWalletPage />} />
          <Route path="emi-calculator" element={<EmiCalculatorPage />} />
          <Route path="kyc" element={<AssociateKycPage />} />
          <Route path="reviews" element={<AssociateReviewsPage />} />
          <Route path="profile" element={<AssociateProfilePage />} />
          <Route path="chat" element={<AssociateChatPage />} />
          <Route path="chat/:threadId" element={<AssociateChatPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}
